import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { Button } from './button';
import { surfaceClassName, useSurface } from './surface.context';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export const DIALOG_SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
} as const;

interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
  size?: keyof typeof DIALOG_SIZES;
  /** Corner "X" button. On by default. */
  showClose?: boolean;
  /** Below `sm`, cover the whole screen edge to edge (no margin, no radius). */
  fullScreenOnMobile?: boolean;
}

const FULL_SCREEN_ON_MOBILE =
  'max-sm:inset-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0';

export function DialogContent({
  className,
  children,
  size = 'md',
  showClose = true,
  fullScreenOnMobile = false,
  ...props
}: DialogContentProps) {
  const surface = useSurface();
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay data-slot="dialog-overlay" className="fixed inset-0 z-dialog bg-overlay backdrop-blur-sm" />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          surfaceClassName(surface),
          'fixed left-1/2 top-1/2 z-dialog-content max-h-[90vh] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto',
          'rounded-card border border-line bg-surface p-6 text-ink shadow-e4 outline-none',
          DIALOG_SIZES[size],
          fullScreenOnMobile && FULL_SCREEN_ON_MOBILE,
          className,
        )}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close asChild>
            {/* No tooltip: when the dialog has nothing else to focus, it opens
                with focus on this X, the tooltip popped up on its own and the
                first Esc only closed the tooltip. */}
            <Button variant="ghost" size="iconSm" aria-label="Cerrar" tooltip="" className="absolute right-3 top-3">
              <X />
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('mb-4 flex flex-col gap-1.5', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-footer" className={cn('mt-5 flex justify-end gap-2', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn('text-lg font-semibold text-ink', className)} {...props} />;
}

export function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description className={cn('text-sm text-ink-2', className)} {...props} />;
}
